/**
 * Server-sent events over `fetch`, the text-protocol half.
 *
 * The browser's `EventSource` cannot send an `Authorization` header, and this console's access
 * token lives in a module variable precisely so nothing but a header can carry it (see
 * `api.ts`). So streams are read with `fetch` + a reader, and this module is the part of that
 * which can be unit-tested: turning an arbitrary sequence of network chunks back into the
 * frames the server wrote. Chunk boundaries land wherever TCP feels like it — mid-line,
 * mid-JSON — which is exactly the thing a test should pin.
 */

/**
 * Split off the complete frames in `buffer`, returning what is left.
 *
 * A frame ends at a blank line (`\n\n`). Whatever follows the last complete frame — usually a
 * partial line of the next one — stays in `rest` and is prepended to the next chunk by the
 * caller.
 */
export function drainSseBuffer(buffer: string): { frames: string[]; rest: string } {
  // Normalise CRLF once so the split below has one delimiter to think about.
  const normalized = buffer.replace(/\r\n/g, "\n");
  const parts = normalized.split("\n\n");
  const rest = parts.pop() ?? "";
  return { frames: parts.filter((frame) => frame.length > 0), rest };
}

/**
 * The payload of one frame, or null for a frame that carries none.
 *
 * Comment lines (`: keepalive`) are the server holding the connection open through proxies —
 * meaningful to the transport, noise to the application. Multiple `data:` lines in one frame
 * are one payload split across lines, per the SSE spec, and are joined with newlines.
 */
export function sseData(frame: string): string | null {
  const data = frame
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).replace(/^ /, ""));
  return data.length > 0 ? data.join("\n") : null;
}
